import { Injectable } from '@angular/core';
import { jsPDF, jsPDFOptions } from 'jspdf';
import { UtilsService } from './utils.service';

@Injectable({
  providedIn: 'root'
})
export class GenerarPdfEstadoCuentaService {

  COLOR_TEXT_TITLE = '#75b7b6';
  COLOR_BLACK = '#000000';
  COLOR_WHITE = '#FFFFFF';
  COLOR_BORDER = '#cfe5e5';
  COLOR_ROW_ALT = '#f7fbfb';
  COLOR_SECTION_BG = '#75b7b6';
  COLOR_LABEL_BG = '#edf7f7';

  MARGIN_X = 14;
  HEADER_SAFE_Y = 56;
  FOOTER_SAFE_Y = 15;

  UTILS = new UtilsService();

  options: jsPDFOptions = {};

  doc: any;
  pageWidth = 0;
  pageHeight = 0;
  contentWidth = 0;

  constructor() {

  }

  async generar(estadoCuenta: any, det: any[] = []) {
    this.doc = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'letter',
      compress: true
    });

    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - (this.MARGIN_X * 2);

    const dataPdf = this.normalizeEstadoCuenta(estadoCuenta, det);
    const enc = dataPdf.Encabezado;
    const cuota = dataPdf.Cuota;
    const resumen = dataPdf.Resumen;
    const pagos = dataPdf.Pagos;

    this.addHeader();
    this.addFooter();

    this.doc.addImage({
      imageData: 'assets/img/logo-credijamar.png',
      format: 'PNG',
      x: (this.pageWidth - 48) / 2,
      y: 14,
      width: 48,
      height: 24
    });

    this.doc.setFont('helvetica', 'normal', 'bold');
    this.doc.setFontSize(16);
    this.doc.setTextColor(this.COLOR_TEXT_TITLE);
    this.doc.text('ESTADO DE CUENTA', this.pageWidth / 2, 45, { align: 'center' });

    this.doc.setFontSize(9);
    this.doc.setTextColor(this.COLOR_BLACK);
    this.doc.text(
      'Fecha de Generacion: ' + this.formatDate(enc.FECHA_GENERACION || (new Date()).toString()),
      this.pageWidth / 2,
      51,
      { align: 'center' }
    );

    let y = this.HEADER_SAFE_Y;

    y = this.drawKeyValueGrid('Datos del cliente', [
      ['Nombre', (enc.NOMBRE || '').toUpperCase()],
      ['Identificacion', enc.IDENTIFICACION_PERSONAL || enc.NRO_IDENTIFICACION || enc.N_IDE || ''],
      ['Cuenta', enc.CUENTA || ''],
      ['Inicio credito', this.formatDate(enc.INICIO_CREDITO)],
      ['Dia corte', enc.DIA_CORTE || enc.FEC_VENC || ''],
      ['Metodo interes', enc.METODO_INTERES || ''],
      ['Devolucion interes', enc.TIPO_DEVOLUCION_INTERES || ''],
      ['Cupo disponible', this.formatValue(enc.CUPO_DISPONIBLE)],
      ['Observaciones', enc.OBSERVACIONES || ''],
    ], y);

    y = this.drawKeyValueGrid('Datos de la factura', [
      ['Factura', cuota.FAC || estadoCuenta?.factura || enc.FACTURA || ''],
      ['Total Cuotas', cuota.CUOTAS || 0],
      ['Valor cuota', this.formatValue(cuota.VALOR_CUOTA)],
      ['Pendientes', cuota.CUOTAS_PENDIENTES || 0],
      ['Pagadas', cuota.CUOTAS_PAGADAS || 0],
      ['Vencidas', cuota.CUOTAS_VENCIDAS || 0],
      ['Saldo vencido', this.formatValue(cuota.SALDO_VENCIDO)],
    ], y + 4);

    y = this.drawKeyValueGrid('Resumen financiero', [
      ['Total financiado', this.formatValue(resumen.TOTAL_FINANCIADO)],
      ['Valor pagado', this.formatValue(resumen.VALOR_PAGADO)],
      ['Saldo', this.formatValue(resumen.SALDO)],
      ['Interes mora corte', this.formatValue(resumen.INTERES_MORA_CORTE)],
      ['Gasto cobranza corte', this.formatValue(resumen.GASTO_COBRANZA_CORTE)],
      ['Otros gastos corte', this.formatValue(resumen.OTROS_GASTOS_CORTE)],
      ['Valor total adeudado', this.formatValue(resumen.VALOR_TOTAL_ADEUDADO)],
    ], y + 4);

    this.drawPayments('Pagos realizados', this.normalizePagos(pagos), y + 4);

    this.doc.save('EstadoCuenta-' + (cuota.FAC || estadoCuenta?.factura || enc.FACTURA || 'reporte'));
  }

  normalizeEstadoCuenta(estadoCuenta: any, det: any[]) {
    if (estadoCuenta?.Encabezado) {
      return {
        Encabezado: estadoCuenta.Encabezado || {},
        Cuota: estadoCuenta.Cuota || {},
        Resumen: estadoCuenta.Resumen || {},
        Pagos: estadoCuenta.Pagos || [],
      };
    }

    return {
      Encabezado: estadoCuenta || {},
      Cuota: {
        FAC: estadoCuenta?.factura || estadoCuenta?.FACTURA || '',
        CUOTAS: det?.length || 0,
        VALOR_CUOTA: estadoCuenta?.CUOTA || 0,
      },
      Resumen: {},
      Pagos: det || [],
    };
  }

  drawKeyValueGrid(title: string, rows: any[][], startY: number) {
    const titleHeight = 7;
    const rowHeight = 8;
    const columns = 2;
    const gap = 3;
    const colWidth = (this.contentWidth - gap) / columns;
    const labelWidth = 35;
    const valueWidth = colWidth - labelWidth;
    const sectionHeight = titleHeight + (Math.ceil(rows.length / columns) * rowHeight);
    let y = this.ensurePageSpace(startY, sectionHeight);

    this.drawSectionTitle(title, y, titleHeight);
    y += titleHeight;

    for (let i = 0; i < rows.length; i += columns) {
      y = this.ensurePageSpace(y, rowHeight);

      for (let col = 0; col < columns; col++) {
        const item = rows[i + col];
        if (!item) {
          continue;
        }

        const x = this.MARGIN_X + (col * (colWidth + gap));
        const bgColor = Math.floor(i / columns) % 2 === 0 ? this.COLOR_WHITE : this.COLOR_ROW_ALT;

        this.doc.setDrawColor(this.COLOR_BORDER);
        this.doc.setFillColor(bgColor);
        this.doc.rect(x, y, colWidth, rowHeight, 'FD');

        this.doc.setFillColor(this.COLOR_LABEL_BG);
        this.doc.rect(x, y, labelWidth, rowHeight, 'FD');

        this.doc.setFont('helvetica', 'normal', 'bold');
        this.doc.setFontSize(7);
        this.doc.setTextColor(this.COLOR_BLACK);
        this.doc.text(String(item[0]), x + (labelWidth / 2), y + 5, { align: 'center' });

        this.doc.setFont('helvetica', 'normal', 'normal');
        this.doc.setFontSize(7);
        this.doc.text(
          this.fitText(item[1], valueWidth - 4),
          x + labelWidth + (valueWidth / 2),
          y + 5,
          { align: 'center' }
        );
      }

      y += rowHeight;
    }

    return y;
  }

  drawPayments(title: string, pagos: any[], startY: number) {
    const titleHeight = 7;
    const headerHeight = 8;
    const detailRowHeight = 8;
    const blockGap = 4;
    const blockHeight = headerHeight + (detailRowHeight * 2) + blockGap;
    let y = this.ensurePageSpace(startY, titleHeight + blockHeight);

    this.drawSectionTitle(title, y, titleHeight);
    y += titleHeight;

    pagos.forEach((pago, index) => {
      const isTotal = pago.NRO_RECIBO_PAGO === 'TOTAL';
      const detailRows = isTotal ? 3 : 2;
      const currentBlockHeight = headerHeight + (detailRowHeight * detailRows) + blockGap;

      if (y + currentBlockHeight > this.pageHeight - this.FOOTER_SAFE_Y) {
        this.doc.addPage();
        this.addHeader();
        this.addFooter();
        y = this.HEADER_SAFE_Y;
        this.drawSectionTitle(title, y, titleHeight);
        y += titleHeight;
      }

      const fillColor = index % 2 === 0 ? this.COLOR_WHITE : this.COLOR_ROW_ALT;

      if (isTotal) {
        this.drawPaymentTotalBlock(y, pago, headerHeight, detailRowHeight);
      } else {
        this.drawPaymentBlock(y, pago, fillColor, headerHeight, detailRowHeight);
      }
      y += currentBlockHeight;
    });
  }

  drawPaymentBlock(y: number, pago: any, fillColor: string, headerHeight: number, detailRowHeight: number) {
    const headerColumns = [
      { label: 'Recibo', value: pago.NRO_RECIBO_PAGO, width: 30 },
      { label: 'Fecha', value: pago.FECHA_PAGO, width: 32 },
      { label: 'Medio de pago', value: pago.MEDIO_PAGO, width: 65 },
      { label: 'Total pagado', value: pago.TOTAL_PAGADO, width: this.contentWidth - 127 },
    ];
    const details = [
      ['Capital', pago.CAPITAL],
      ['Interes', pago.INTERES],
      ['Seguro financiero', pago.SEGURO_FINANCIERO],
      ['Interes mora', pago.INTERES_MORA],
      ['Gastos cobranza', pago.GASTOS_COBRANZA],
      ['Honorarios', pago.HONORARIOS],
      ['Seguro riesgo', pago.SEGURO_RIESGO],
      ['Abono credito', pago.ABONO_CREDITO],
    ];
    const detailColumns = 4;
    const detailWidth = this.contentWidth / detailColumns;

    this.doc.setDrawColor(this.COLOR_BORDER);
    this.doc.setLineWidth(0.15);
    this.doc.setFillColor(fillColor);
    this.doc.rect(this.MARGIN_X, y, this.contentWidth, headerHeight + (detailRowHeight * 2), 'FD');

    let x = this.MARGIN_X;
    headerColumns.forEach(column => {
      this.doc.setFillColor(this.COLOR_LABEL_BG);
      this.doc.rect(x, y, column.width, headerHeight, 'FD');
      this.doc.setFont('helvetica', 'normal', 'bold');
      this.doc.setFontSize(6.4);
      this.doc.setTextColor(this.COLOR_TEXT_TITLE);
      this.doc.text(this.fitText(column.label, column.width - 2), x + 2, y + 3.3, { align: 'left' });

      this.doc.setFont('helvetica', 'normal', 'normal');
      this.doc.setFontSize(6.6);
      this.doc.setTextColor(this.COLOR_BLACK);
      this.doc.text(this.fitText(column.value, column.width - 2), x + (column.width / 2), y + 6.6, { align: 'center' });
      x += column.width;
    });

    for (let i = 0; i < details.length; i++) {
      const row = Math.floor(i / detailColumns);
      const col = i % detailColumns;
      const cellX = this.MARGIN_X + (col * detailWidth);
      const cellY = y + headerHeight + (row * detailRowHeight);
      const label = details[i][0];
      const value = details[i][1];

      this.doc.setDrawColor(this.COLOR_BORDER);
      this.doc.setFillColor(fillColor);
      this.doc.rect(cellX, cellY, detailWidth, detailRowHeight, 'FD');

      if (!label) {
        continue;
      }

      this.doc.setFont('helvetica', 'normal', 'bold');
      this.doc.setFontSize(6.1);
      this.doc.setTextColor(this.COLOR_TEXT_TITLE);
      this.doc.text(this.fitText(label, detailWidth - 4), cellX + 2, cellY + 3.2, { align: 'left' });

      this.doc.setFont('helvetica', 'normal', 'normal');
      this.doc.setFontSize(6.5);
      this.doc.setTextColor(this.COLOR_BLACK);
      this.doc.text(this.fitText(value, detailWidth - 4), cellX + (detailWidth / 2), cellY + 6.5, { align: 'center' });
    }
  }

  drawPaymentTotalBlock(y: number, pago: any, headerHeight: number, detailRowHeight: number) {
    const details = [
      ['Capital', pago.CAPITAL],
      ['Interes', pago.INTERES],
      ['Seguro financiero', pago.SEGURO_FINANCIERO],
      ['Interes mora', pago.INTERES_MORA],
      ['Gastos cobranza', pago.GASTOS_COBRANZA],
      ['Honorarios', pago.HONORARIOS],
      ['Seguro riesgo', pago.SEGURO_RIESGO],
      ['Total pagado', pago.TOTAL_PAGADO],
      ['Abono credito', pago.ABONO_CREDITO],
    ];
    const detailColumns = 4;
    const detailWidth = this.contentWidth / detailColumns;
    const detailRows = Math.ceil(details.length / detailColumns);

    this.doc.setDrawColor(this.COLOR_BORDER);
    this.doc.setLineWidth(0.15);
    this.doc.setFillColor(this.COLOR_SECTION_BG);
    this.doc.rect(this.MARGIN_X, y, this.contentWidth, headerHeight, 'FD');
    this.doc.setFont('helvetica', 'normal', 'bold');
    this.doc.setFontSize(7);
    this.doc.setTextColor(this.COLOR_WHITE);
    this.doc.text('TOTAL', this.pageWidth / 2, y + 5.4, { align: 'center' });

    for (let i = 0; i < detailRows * detailColumns; i++) {
      const row = Math.floor(i / detailColumns);
      const col = i % detailColumns;
      const cellX = this.MARGIN_X + (col * detailWidth);
      const cellY = y + headerHeight + (row * detailRowHeight);
      const detail = details[i];

      this.doc.setDrawColor(this.COLOR_BORDER);
      this.doc.setFillColor(this.COLOR_WHITE);
      this.doc.rect(cellX, cellY, detailWidth, detailRowHeight, 'FD');

      if (!detail) {
        continue;
      }

      const label = detail[0];
      const value = detail[1];

      this.doc.setFont('helvetica', 'normal', 'bold');
      this.doc.setFontSize(6.1);
      this.doc.setTextColor(this.COLOR_TEXT_TITLE);
      this.doc.text(this.fitText(label, detailWidth - 4), cellX + 2, cellY + 3.2, { align: 'left' });

      this.doc.setFont('helvetica', 'normal', 'bold');
      this.doc.setFontSize(6.5);
      this.doc.setTextColor(this.COLOR_BLACK);
      this.doc.text(this.fitText(value, detailWidth - 4), cellX + (detailWidth / 2), cellY + 6.5, { align: 'center' });
    }
  }

  drawTableHeader(columns: any[], x: number, y: number, height: number) {
    columns.forEach(column => {
      this.doc.setDrawColor(this.COLOR_BORDER);
      this.doc.setFillColor(this.COLOR_TEXT_TITLE);
      this.doc.rect(x, y, column.width, height, 'FD');
      this.doc.setFont('helvetica', 'normal', 'bold');
      this.doc.setFontSize(5.8);
      this.doc.setTextColor(this.COLOR_WHITE);
      this.doc.text(
        this.fitText(column.label, column.width - 2),
        x + (column.width / 2),
        y + 5.5,
        { align: 'center' }
      );
      x += column.width;
    });
  }

  drawSectionTitle(title: string, y: number, height: number) {
    this.doc.setDrawColor(this.COLOR_BORDER);
    this.doc.setFillColor(this.COLOR_SECTION_BG);
    this.doc.rect(this.MARGIN_X, y, this.contentWidth, height, 'FD');
    this.doc.setFont('helvetica', 'normal', 'bold');
    this.doc.setFontSize(9);
    this.doc.setTextColor(this.COLOR_WHITE);
    this.doc.text(title, this.pageWidth / 2, y + 4.8, { align: 'center' });
    this.doc.setTextColor(this.COLOR_BLACK);
  }

  normalizePagos(pagos: any[]) {
    if (!pagos || pagos.length === 0) {
      return [{
        NRO_RECIBO_PAGO: 'Sin pagos',
        FECHA_PAGO: '',
        MEDIO_PAGO: '',
        CAPITAL: '',
        INTERES: '',
        SEGURO_FINANCIERO: '',
        INTERES_MORA: '',
        GASTOS_COBRANZA: '',
        HONORARIOS: '',
        SEGURO_RIESGO: '',
        TOTAL_PAGADO: '',
        ABONO_CREDITO: '',
      }];
    }

    return pagos.map(pago => ({
      NRO_RECIBO_PAGO: pago.NRO_RECIBO_PAGO || '',
      FECHA_PAGO: this.formatDate(pago.FECHA_PAGO),
      MEDIO_PAGO: pago.MEDIO_PAGO || '',
      CAPITAL: this.formatValue(pago.CAPITAL),
      INTERES: this.formatValue(pago.INTERES),
      SEGURO_FINANCIERO: this.formatValue(pago.SEGURO_FINANCIERO),
      INTERES_MORA: this.formatValue(pago.INTERES_MORA),
      GASTOS_COBRANZA: this.formatValue(pago.GASTOS_COBRANZA),
      HONORARIOS: this.formatValue(pago.HONORARIOS),
      SEGURO_RIESGO: this.formatValue(pago.SEGURO_RIESGO),
      TOTAL_PAGADO: this.formatValue(pago.TOTAL_PAGADO),
      ABONO_CREDITO: this.formatValue(pago.ABONO_CREDITO),
    }));
  }

  formatValue(value: any) {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (typeof value === 'number') {
      return this.UTILS.formatCurrency(value);
    }

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && `${value}`.trim() !== '') {
      return this.UTILS.formatCurrency(numericValue);
    }

    return String(value);
  }

  formatDate(value: any) {
    if (!value) {
      return '';
    }

    return this.UTILS.formatDate(value, 'dd/MM/yyyy', '+0000') || '';
  }

  fitText(value: any, maxWidth: number) {
    const text = value === null || value === undefined ? '' : String(value);

    if (this.doc.getTextWidth(text) <= maxWidth) {
      return text;
    }

    let shortened = text;
    while (shortened.length > 3 && this.doc.getTextWidth(shortened + '...') > maxWidth) {
      shortened = shortened.slice(0, -1);
    }

    return shortened.length > 3 ? shortened + '...' : shortened;
  }

  ensurePageSpace(y: number, neededHeight: number) {
    if (y + neededHeight <= this.pageHeight - this.FOOTER_SAFE_Y) {
      return y;
    }

    this.doc.addPage();
    this.addHeader();
    this.addFooter();
    return this.HEADER_SAFE_Y;
  }

  addHeader() {
    this.doc.addImage({
      imageData: 'assets/img/cabecera.png',
      format: 'PNG',
      x: 0,
      y: 0,
      width: this.pageWidth + 10,
      height: 55
    });
  }

  addFooter() {
    this.doc.addImage({
      imageData: 'assets/img/cabecera.png',
      format: 'PNG',
      x: this.pageWidth + 10,
      y: this.pageHeight - 55,
      width: this.pageWidth + 12,
      height: 55,
      rotation: 180
    });
  }
}
